import { cn } from '@/lib/ui/cn';
import { UITileProps } from '@/types/ui';
import { getImageSizes } from '@/utils/tailwind';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import { Tint } from './Tint';

const tileVariants = cva(
  'rounded-4xl font-sans font-medium w-full relative overflow-hidden z-0',
  {
    variants: {
      visibility: {
        none: '',
        sm: 'hidden sm:block',
        md: 'hidden md:block',
        lg: 'hidden lg:block',
        xl: 'hidden xl:block',
        '2xl': 'hidden 2xl:block',
      },
      theme: {
        light: 'text-sh-black',
        dark: 'text-sh-white',
      },
      background: {
        light: 'bg-sh-paper',
        dark: 'bg-sh-cream',
        image: 'bg-transparent',
      },
      aspect: {
        square: 'aspect-square',
        landscape: 'aspect-[4/3]',
        portrait: 'aspect-[3/4]',
        'near-square': 'aspect-[6/5]',
        full: 'h-full',
      },
    },
    defaultVariants: {},
  }
);

export function Tile({
  image,
  imageAlt = '',
  theme,
  tint = true,
  aspectRatio,
  children,
  containerClass,
  className,
  visibility,
  ...props
}: UITileProps) {
  return (
    <div
      className={cn(
        tileVariants({
          theme,
          visibility: visibility ?? 'none',
          background: !!image ? 'image' : theme,
          aspect: aspectRatio ?? 'square',
        }),
        containerClass
      )}
      {...props}
    >
      {image && (
        <>
          <Image
            src={image}
            alt={imageAlt}
            fill
            className='-z-10 object-cover'
            sizes={getImageSizes(
              [
                ['md', '100vw'],
                ['xl', '50vw'],
              ],
              '33vw'
            )}
          />
          <Tint tint={tint} textColor={theme} />
        </>
      )}
      <div className={cn('relative z-10 h-full', className)}>{children}</div>
    </div>
  );
}
